// ============================================================================
// Finance view — shared chrome (CW-Q). Band, tile, bullet, basis footer, and the
// "removed tile" placeholder.
//
// The placeholder is the important one. Jose's rule: "Where a tile cannot be
// filled, REMOVE it and name what it would take." So there is exactly one way
// to render an unfillable panel, it always names its prerequisite, and there is
// no code path in this module that renders a number it did not measure.
// ============================================================================

import type { ReactNode } from 'react';
import { Info, Lock } from 'lucide-react';
import {
  BAND_CLASSES,
  BULLET_SCALE_LABEL,
  BULLET_SCALE_MAX,
  BULLET_SCALE_MIN,
  bulletPosition,
  fmtPct,
  marginBand,
  type MarginThresholds,
} from './finance-lib';

// ----- Band ------------------------------------------------------------------

export function Band({
  n,
  title,
  subtitle,
  children,
  actions,
}: {
  n: number;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="mt-6 first:mt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-tcs-text">
          <span className="mr-2 font-mono text-xs text-tcs-text-muted">{n}</span>
          {title}
        </h2>
        {actions}
      </div>
      {subtitle && (
        <p className="mt-1 max-w-4xl text-xs leading-relaxed text-tcs-text-secondary">
          {subtitle}
        </p>
      )}
      <div className="mt-3">{children}</div>
    </section>
  );
}

// ----- Tile ------------------------------------------------------------------

export function Tile({
  label,
  value,
  sub,
  hint,
  tone,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  hint?: string;
  tone?: 'plain' | 'green' | 'amber' | 'red' | 'unknown';
}) {
  const band = tone && tone !== 'plain' ? BAND_CLASSES[tone] : null;
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
        <span>{label}</span>
        {hint && (
          <span title={hint} className="cursor-help">
            <Info className="h-3 w-3" aria-hidden />
            <span className="sr-only">{hint}</span>
          </span>
        )}
      </div>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${
          band ? band.text : 'text-tcs-text'
        }`}
      >
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-tcs-text-secondary">{sub}</div>}
    </div>
  );
}

// ----- Bullet ----------------------------------------------------------------
// 20–34% scale, labelled once per group. The target tick is drawn from the DB
// threshold, so a band change in app_settings moves the tick.

export function Bullet({
  label,
  marginPctValue,
  thresholds,
  right,
}: {
  label: ReactNode;
  marginPctValue: number | null;
  thresholds: MarginThresholds;
  right?: ReactNode;
}) {
  const pos = bulletPosition(marginPctValue);
  const band = marginBand(marginPctValue, thresholds);
  const cls = BAND_CLASSES[band];
  const span = BULLET_SCALE_MAX - BULLET_SCALE_MIN;
  const targetPct =
    ((thresholds.target_pct - BULLET_SCALE_MIN) / span) * 100;
  const amberPct =
    ((thresholds.amber_floor_pct - BULLET_SCALE_MIN) / span) * 100;

  return (
    <div className="grid grid-cols-[minmax(7rem,11rem)_1fr_auto] items-center gap-3 py-1.5">
      <div className="min-w-0 truncate text-sm text-tcs-text">{label}</div>
      <div className="relative h-4 rounded bg-tcs-bg-muted">
        {/* Amber floor and target ticks, drawn from the DB thresholds. */}
        <span
          className="absolute top-0 h-4 w-px bg-tcs-border-strong"
          style={{ left: `${Math.max(0, Math.min(100, amberPct))}%` }}
          aria-hidden
        />
        <span
          className="absolute top-0 h-4 w-0.5 bg-tcs-text"
          style={{ left: `${Math.max(0, Math.min(100, targetPct))}%` }}
          aria-hidden
        />
        {marginPctValue !== null && (
          <span
            className={`absolute top-1 h-2 rounded-sm ${cls.bg} ${cls.border} border`}
            style={{ left: 0, width: `${pos.pct}%` }}
            aria-hidden
          />
        )}
        {pos.outOfScaleHigh && (
          <span className="absolute right-1 top-0 text-[9px] leading-4 text-tcs-text-muted">
            ▸ off scale
          </span>
        )}
        {pos.outOfScaleLow && (
          <span className="absolute left-1 top-0 text-[9px] leading-4 text-tcs-text-muted">
            ◂ off scale
          </span>
        )}
        {marginPctValue === null && (
          <span className="absolute left-1 top-0 text-[9px] leading-4 text-tcs-text-muted">
            no recorded cost
          </span>
        )}
      </div>
      <div className={`w-28 text-right text-sm font-medium tabular-nums ${cls.text}`}>
        {right ?? fmtPct(marginPctValue)}
      </div>
    </div>
  );
}

export function BulletScaleLegend({ thresholds }: { thresholds: MarginThresholds }) {
  return (
    <p className="mt-2 text-[11px] text-tcs-text-muted">
      {BULLET_SCALE_LABEL} Heavy tick = target {fmtPct(thresholds.target_pct)}; light
      tick = amber floor {fmtPct(thresholds.amber_floor_pct)}. Both read from
      <code className="mx-1 rounded bg-tcs-bg-muted px-1">app_settings</code>.
    </p>
  );
}

// ----- Removed tile ----------------------------------------------------------

export function RemovedTile({
  title,
  prerequisite,
}: {
  title: string;
  prerequisite: string;
}) {
  return (
    <div className="rounded-lg border border-dashed border-tcs-border-strong bg-tcs-bg-muted p-3">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">
        <Lock className="h-3 w-3" aria-hidden />
        <span>{title} — not shown</span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-tcs-text-secondary">
        <span className="font-medium text-tcs-text">What it would take: </span>
        {prerequisite}
      </p>
      <p className="mt-1 text-[11px] text-tcs-text-muted">
        This panel is removed rather than estimated. A plausible number here would
        be indistinguishable from a measured one.
      </p>
    </div>
  );
}

// ----- Basis footer ----------------------------------------------------------

export function BasisFooter({
  monthName,
  buildings,
  revenue,
  subCost,
  marginPctText,
  reconciliation,
}: {
  monthName: string;
  buildings: number;
  revenue: string;
  subCost: string;
  marginPctText: string;
  reconciliation?: string;
}) {
  return (
    <footer className="mt-8 border-t border-tcs-border pt-3 text-[11px] leading-relaxed text-tcs-text-muted">
      <p>
        <span className="font-medium text-tcs-text-secondary">
          Source and basis — {monthName}.
        </span>{' '}
        TCS database, <code>facilities</code> and <code>service_assignments</code>,
        active assignments summed, shared accounts split by manager, as recorded.
        Hospitals at July 2026 AR Logbook actuals. Monthly revenue from{' '}
        <code>forecasts</code> / <code>forecast_lines</code>. Labour margin excludes
        the management fee, usables and the auto-scrubber lease, per the August close
        rules. ARR is the current monthly book × 12 — a run rate, not contracted
        backlog.
      </p>
      <p className="mt-1">
        <span className="font-medium text-tcs-text-secondary">Tie-out:</span>{' '}
        <span className="tabular-nums">
          {buildings} buildings · {revenue} revenue · {subCost} sub cost ·{' '}
          {marginPctText} labour margin
        </span>
        . Page 1 and the Portfolio header aggregate the same view and must agree to
        the cent.
      </p>
      {reconciliation && <p className="mt-1">{reconciliation}</p>}
    </footer>
  );
}
